import { IEntitlementRecord } from './types'

/** Query param the Entel activation link carries (PRO-13). Read-only for us: it is never registered with nuqs and never written back to the URL. */
export const ENTITLEMENT_PARAM = 'entitlementId'

/**
 * How long a captured id stays usable. Long enough for the slow half of the
 * journey (password-recovery mail read hours later), short enough that a
 * leftover id from an abandoned attempt does not silently attach itself to an
 * unrelated session weeks later — the ticket's explicit acceptance criterion.
 */
export const ENTITLEMENT_TTL_MS = 24 * 60 * 60 * 1000

/**
 * The id ends up in an auth payload (PRO-14), so junk must die at the door:
 * empty, whitespace, oversized or exotic-charset values are never stored.
 * Bound chosen loose — ids are opaque partner identifiers.
 */
const ENTITLEMENT_ID_PATTERN = /^[A-Za-z0-9_-]{1,128}$/

/**
 * The single normalization point: canonical (trimmed) id, or null. Every
 * boundary — URL read, capture, stored-record parse, consumed check — goes
 * through this, so "validated" and "canonical" can never diverge: a padded id
 * is either canonicalized here or rejected here, never persisted or compared
 * as-is.
 */
export const normalizeEntitlementId = (value: unknown): string | null => {
  if (typeof value !== 'string') return null

  const trimmed = value.trim()
  return ENTITLEMENT_ID_PATTERN.test(trimmed) ? trimmed : null
}

/**
 * First valid `entitlementId` in a search string, or null. Scans every
 * occurrence — a tracking/mail-client rewrite that produces
 * `?entitlementId=&entitlementId=X` must not mask the valid value behind an
 * empty first one.
 */
export const readEntitlementIdFromSearch = (search: string): string | null => {
  for (const raw of new URLSearchParams(search).getAll(ENTITLEMENT_PARAM)) {
    const normalized = normalizeEntitlementId(raw)

    if (normalized) return normalized
  }

  return null
}

/**
 * Parses a stored record, rejecting anything that is not the exact persisted
 * shape and returning the id in canonical (trimmed) form — a padded id in
 * storage (legacy write, tampering) must not defeat the tombstone's identity
 * comparison. Pure by design: corruption telemetry lives at the store layer.
 */
export const parseEntitlementRecord = (stored: string): IEntitlementRecord | null => {
  if (!stored) return null

  try {
    const parsed: unknown = JSON.parse(stored)

    if (typeof parsed !== 'object' || parsed === null) return null

    const record = parsed as Partial<IEntitlementRecord>
    const id = normalizeEntitlementId(record.id)

    if (!id || typeof record.capturedAt !== 'number') return null

    // A non-numeric consumedAt would read as "consumed" (`!== undefined`) and
    // silently discard a live id — corrupted input is this parser's whole job.
    if (record.consumedAt !== undefined && typeof record.consumedAt !== 'number') return null

    return { id, capturedAt: record.capturedAt, consumedAt: record.consumedAt }
  } catch {
    // Corrupted storage — treated as absent; reported by the store's read.
    return null
  }
}

/**
 * One expiry clock for the whole record, consumed or not: everything dies
 * `ENTITLEMENT_TTL_MS` after capture. This is what bounds the consumed
 * tombstone too — a partner legitimately re-issuing the same id weeks later
 * must not be blocked by residue from a long-finished journey.
 */
export const isEntitlementRecordExpired = (record: IEntitlementRecord, now: number): boolean => {
  const age = now - record.capturedAt

  // A future capturedAt (device clock was ahead at capture, corrected since)
  // must expire too — a negative age would otherwise never reach the TTL and
  // the record would live forever, the exact outcome the TTL exists to bound.
  return age < 0 || age >= ENTITLEMENT_TTL_MS
}

/**
 * The whole server-side footprint of the activation payload (PRO-14), for the
 * auth proxy routes: re-normalizes the id a client put in the raw body (junk
 * injected there must die here just as it does at capture) and wraps it under
 * the backend's field name — auth detects `data.merchantEntitlementId`, links
 * via /orders/linking/bango and returns `orderLinked` in the response. No id,
 * no field: spread the result into the proxied body.
 */
export const toActivationLinkingBody = (rawId: unknown): { data: { merchantEntitlementId: string } } | Record<string, never> => {
  const merchantEntitlementId = normalizeEntitlementId(rawId)

  return merchantEntitlementId ? { data: { merchantEntitlementId } } : {}
}

/** Usable = not consumed and not expired. */
export const isEntitlementRecordUsable = (record: IEntitlementRecord | null, now: number): boolean => {
  if (!record) return false
  if (record.consumedAt !== undefined) return false

  return !isEntitlementRecordExpired(record, now)
}
